import { ApplicationConfig, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { appReducer } from './ngrx';
import { customDomainRoutes, mainRoutes } from './app.routes';
import { CompanyEffects } from './ngrx/company/effects/company.effect';
import { UserEffects } from './ngrx/user/effects/user.effect';
import { UIEffects } from './ngrx/ui/effects/ui.effect';
import { NotificationEffects } from './ngrx/notification/effects/notification.effect';
import { AuthEffects } from './auth/effects/auth.effect';
import { TranslateModule } from '@ngx-translate/core';
import { provideClientHydration } from '@angular/platform-browser';
import { ErrorHandler, PLATFORM_ID, APP_ID, inject, importProvidersFrom } from '@angular/core';
import { isPlatformServer, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  RouteReuseStrategy,
  provideRouter,
  Router,
  withViewTransitions,
  withInMemoryScrolling
} from '@angular/router';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { AppErrorHandler } from './shared/services/error-handler/error-handler.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { PaginatorTranslations } from './shared/services/paginator-translations/paginator-translations';
import { CoreHttpInterceptor } from './shared/services/core-request/core.interceptor.service';
import { CustomRouteReuseStrategy } from './shared/services/reuse-strategy/custom-reuse-strategy';
import { Angulartics2GoogleAnalytics, Angulartics2Module } from 'angulartics2';
import { browserLanguageFactory } from './platform/localization/browser-language/browser-language.factory';
import { ClipboardService } from 'ngx-clipboard';
import { BROWSER_LANGUAGE_TOKEN } from './platform/localization/browser-language/browser-language.token';
import { environment } from '../environments/environment';
import { CustomDomainService } from './core/company/custom-domain/custom-domain.service';
import { COOKIES_TOKEN } from './platform/cookies/cookies.token';
import { cookiesFactory } from './platform/cookies/cookies.factory';
import { provideLottieOptions } from 'ngx-lottie';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import localeNb from '@angular/common/locales/nb';


registerLocaleData(localeNb);

// App initializer function for dynamic route configuration
function appInitializer(customDomainService: CustomDomainService, router: Router): () => Promise<void> {
  return async () => {
    const angulartics2GoogleAnalytics = inject(Angulartics2GoogleAnalytics);
    angulartics2GoogleAnalytics.startTracking();

    const selectedRoutes = customDomainService.isCustomDomain() ? customDomainRoutes : mainRoutes;
    router.resetConfig(selectedRoutes);
  };
}

// Initialize the global error handler
export function initEventHandler(platformId: object): ErrorHandler {
  const isServer = isPlatformServer(platformId);

  if (isServer) {
    return new ErrorHandler();
  }

  return new AppErrorHandler();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),

    // Router
    provideRouter(mainRoutes, withViewTransitions(), withInMemoryScrolling({
      scrollPositionRestoration: 'enabled',
    })),

    // App Initializers (for dynamic route configuration)
    // ...getAppInitializerProviders(),

    // HTTP
    provideHttpClient(withInterceptorsFromDi(), withFetch()),

    // Animations
    provideAnimations(),

    // Client Hydration
    provideClientHydration(),

    // NgRx Store
    provideStore(appReducer, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),

    provideLottieOptions({
      player: () => import('lottie-web'),
    }),

    // NgRx Effects
    provideEffects([
      CompanyEffects,
      UserEffects,
      UIEffects,
      NotificationEffects,
      AuthEffects
    ]),

    // Only enable devtools in development
    ...(!environment.production ? [provideStoreDevtools({ maxAge: 50 })] : []),

    // Translate (using importProvidersFrom for modules that don't have standalone providers)
    importProvidersFrom(
      TranslateModule.forRoot()
    ),
    provideTranslateHttpLoader({
      prefix: '/assets/i18n/',
      suffix: `.json?cb=${environment.langCacheHash}`
    }),


    // Analytics
    importProvidersFrom(Angulartics2Module.forRoot()),

    // Application Services
    ClipboardService,

    // Tokens and Factories
    { provide: APP_ID, useValue: 'serverApp' },
    {
      provide: BROWSER_LANGUAGE_TOKEN,
      useFactory: browserLanguageFactory,
      deps: [PLATFORM_ID],
    },
    {
      provide: COOKIES_TOKEN,
      useFactory: cookiesFactory
    },

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CoreHttpInterceptor,
      multi: true,
    },

    // Error Handler
    {
      provide: ErrorHandler,
      useFactory: initEventHandler,
      deps: [PLATFORM_ID],
    },

    // Google Tag Manager
    { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },

    // Material Components
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorTranslations,
    },

    // Route Reuse Strategy
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },

    // app initializer
    provideAppInitializer(() => {
      const customDomainService = inject(CustomDomainService);
      const router = inject(Router);
      return appInitializer(customDomainService, router)();
    }),
  ],
};


